import { Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import { useExecutor } from "../../../../../../hooks/useExecutor";
import useNotifications from "../../../../../../hooks/useNotifications";
import { usePractitionerReference } from "../../../../../../hooks/usePractitioner";
import {
  TCopilot,
  TCopilotNotePopulationStatus,
} from "../../../../../../types/copilot";
import {
  useOrganization,
  useOrganizationReference,
} from "../../../../../facility/hooks/useOrganization";
import CopilotNoteAction from "./Notes/CopilotNoteAction";
import CopilotNoteDisplay from "./Notes/CopilotNoteDisplay";
import { CopilotManager } from "../../../../../../managers/CopilotManager";
import { startCase } from "lodash";
import { useCopilotClinicalContext } from "../useCopilotClinicalContext";
import { getAllergiesPopulateNotification } from "./utils/processCopilotAllergies";
import {
  isAllergiesHumanEdited,
  clearSectionsHumanEditedAfterApply,
  getSectionsForPopulationField,
  shouldShowSectionAcceptButton,
} from "./utils/copilotExtensions";
import { getVitalsPopulateNotification } from "./utils/processCopilotVitals";

interface PopulationTask {
  data: any;
  populateFn: () => Promise<void> | void;
  fieldName: keyof TCopilotNotePopulationStatus;
  refreshHistory?: boolean;
}

const CopilotNote = ({
  isRecording,
  readOnly,
  isGeneratingNote = false,
  onGeneratingChange,
}: {
  isRecording: boolean;
  readOnly: boolean;
  /** Parent-owned overlay flag; survives tab unmount/remount. */
  isGeneratingNote?: boolean;
  onGeneratingChange?: (generating: boolean) => void;
}) => {
  const practitionerReference = usePractitionerReference();
  const {
    encounterReference,
    patientReference,
    refreshPatientHistory,
    setEncounterToInProgress,
    copilotData,
    setCopilotData,
    hasLinkedEncounter,
    encounterHeaders,
  } = useCopilotClinicalContext();
  const organizationReference = useOrganizationReference();
  const { executeCallback } = useExecutor();
  const { setNotification } = useNotifications();
  const [populating, setPopulating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [populationStatus, setPopulationStatus] = useState(
    copilotData?.note?.populationStatus,
  );

  const { countryCode } = useOrganization();
  const { enableAiNoteAcceptance } = useFlags();

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPopulationStatus(copilotData?.note?.populationStatus);
  }, [copilotData?.note?.populationStatus]);

  const updatePopulationStatus = (
    field: keyof TCopilotNotePopulationStatus,
    status: boolean,
  ) => {
    setPopulationStatus((prev) => ({
      ...(prev || {}),
      [field]: status,
    }));
  };

  const populateField = async (
    data: any,
    populateFn: () => Promise<void> | void,
    fieldName: keyof TCopilotNotePopulationStatus,
    status: TCopilotNotePopulationStatus,
    refreshHistory?: () => void,
  ) => {
    if (!data || !populateFn || !fieldName) return;

    const sections = getSectionsForPopulationField(fieldName);
    if (
      !shouldShowSectionAcceptButton(copilotData, {
        populationStatusField: fieldName,
        sections,
      })
    ) {
      return;
    }

    try {
      await populateFn();
      if (refreshHistory && typeof refreshHistory === "function") {
        refreshHistory();
      }
      status[fieldName] = true;
      updatePopulationStatus(fieldName, true);
      if (setNotification) {
        setNotification({
          message: `${startCase(fieldName)} populated`,
          variant: "success",
        });
      }
    } catch (error) {
      console.error(`Error populating ${fieldName}`, error);
      if (setNotification) {
        setNotification({
          message: `Error populating ${startCase(fieldName)}`,
          variant: "error",
        });
      }
    }
  };

  const generateNote = async () => {
    if (
      !copilotData?.id ||
      generating ||
      isGeneratingNote ||
      populating ||
      !practitionerReference
    )
      return;

    // Standalone AI notes have a patient on the copilot; encounter notes use context refs.
    const hasPatient =
      !!patientReference?.identifier || !!copilotData?.subject?.identifier;
    if (!hasPatient) {
      setNotification({
        message:
          "This AI note has no patient. Open the note from a patient record or recreate it with a patient selected.",
        variant: "error",
      });
      return;
    }

    try {
      setGenerating(true);
      onGeneratingChange?.(true);

      const response = await CopilotManager.generateNote(
        copilotData.id,
        countryCode,
      );

      setCopilotData(response.data);
      setNotification({
        message: `The copilot note has been ${
          !copilotData?.note ? "generated" : "regenerated"
        }.`,
        variant: "success",
      });
      if (encounterReference) {
        setEncounterToInProgress();
      }
    } catch (error) {
      console.error(error);
      setNotification({
        message: `An error occurred when ${
          !copilotData?.note ? "generating" : "regenerating"
        } the copilot note.`,
        variant: "error",
      });
    } finally {
      setGenerating(false);
      onGeneratingChange?.(false);
    }
  };

  const populateEncounter = async () => {
    if (
      !enableAiNoteAcceptance ||
      !copilotData?.note ||
      generating ||
      populating ||
      !practitionerReference?.identifier ||
      !hasLinkedEncounter ||
      !patientReference?.identifier ||
      !organizationReference?.identifier ||
      !encounterReference?.identifier
    ) {
      if (enableAiNoteAcceptance && !hasLinkedEncounter) {
        setNotification({
          message:
            "Link this note to a medical encounter to apply content to the chart.",
          variant: "error",
        });
      }
      return;
    }

    const note = copilotData.note;
    const encounterId = encounterReference.identifier;
    const organizationId = organizationReference.identifier;
    const practitionerId = practitionerReference.identifier;

    setPopulating(true);

    const nextStatus: TCopilotNotePopulationStatus = {
      ...(note.populationStatus || {}),
    };

    const hasPhysicalAssessment =
      !!note.physicalAssessment &&
      typeof note.physicalAssessment === "object" &&
      Object.values(note.physicalAssessment).some(
        (value) => value !== null && value !== "",
      );

    const hasReviewOfSystems =
      !!note.reviewOfSystems &&
      typeof note.reviewOfSystems === "object" &&
      Object.values(note.reviewOfSystems).some(
        (value) => value !== null && value !== "",
      );

    const hasDiagnoses =
      Array.isArray(note.diagnoses) && note.diagnoses.some((d) => !!d?.code);

    const refreshCard = async (callbackName: string) => {
      if (patientReference.identifier && encounterId) {
        await executeCallback(
          callbackName,
          null,
          patientReference.identifier,
          encounterId,
        );
      }
    };

    const populationTasks: PopulationTask[] = [
      {
        data: note.chiefComplaint,
        populateFn: async () => {
          await CopilotManager.populateChiefComplaint(
            encounterId,
            organizationId,
            practitionerId,
            countryCode,
          );
          await refreshCard("copilot_refresh_chief_complaint");
        },
        fieldName: "chiefComplaint",
      },
      {
        data: note.hpi,
        populateFn: async () => {
          await CopilotManager.populateHpi(
            encounterId,
            organizationId,
            practitionerId,
            countryCode,
          );
          await refreshCard("copilot_refresh_chief_complaint");
        },
        fieldName: "hpi",
      },
      {
        data: note.vitals,
        populateFn: async () => {
          const result = await CopilotManager.populateVitals(
            encounterId,
            organizationId,
            countryCode,
          );
          const notification = getVitalsPopulateNotification(result);
          if (notification.variant !== "success") {
            throw new Error(notification.message);
          }
          await refreshCard("copilot_refresh_vitals");
        },
        fieldName: "vitals",
      },
      {
        data: note.history?.allergies?.length ? note.history.allergies : null,
        populateFn: async () => {
          const result = await CopilotManager.populateAllergies(
            encounterId,
            organizationId,
            countryCode,
            { overwriteReactions: isAllergiesHumanEdited(copilotData) },
          );
          const notification = getAllergiesPopulateNotification(result);
          if (notification.variant !== "success") {
            throw new Error(notification.message);
          }
          await refreshCard("copilot_refresh_allergies");
        },
        fieldName: "allergies",
      },
      {
        data: note.history?.family?.length ? note.history.family : null,
        populateFn: async () => {
          await CopilotManager.populateFamilyHistory(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_history");
        },
        fieldName: "familyHistory",
        refreshHistory: true,
      },
      {
        data: note.history?.hospitalizations?.length
          ? note.history.hospitalizations
          : null,
        populateFn: async () => {
          await CopilotManager.populateHospitalization(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_history");
        },
        fieldName: "hospitalization",
        refreshHistory: true,
      },
      {
        data: note.history?.medical?.length ? note.history.medical : null,
        populateFn: async () => {
          await CopilotManager.populateMedicalHistory(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_history");
        },
        fieldName: "medicalHistory",
        refreshHistory: true,
      },
      {
        data: hasPhysicalAssessment,
        populateFn: async () => {
          await CopilotManager.populatePhysicalAssessment(
            note.physicalAssessment!,
            patientReference,
            encounterReference,
            practitionerReference,
            copilotData.id,
          );
          await refreshCard("copilot_refresh_physical_assessment");
        },
        fieldName: "physicalAssessment",
      },
      {
        data: hasReviewOfSystems,
        populateFn: async () => {
          await CopilotManager.populateReviewOfSystems(
            note.reviewOfSystems!,
            patientReference,
            encounterReference,
            practitionerReference,
            copilotData.id,
          );
          await refreshCard("copilot_refresh_review_of_systems");
        },
        fieldName: "reviewOfSystems",
      },
      {
        data: hasDiagnoses ? note.diagnoses : null,
        populateFn: async () => {
          await CopilotManager.populateDiagnoses(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_diagnoses");
        },
        fieldName: "diagnoses",
      },
      {
        data: note.plan?.details,
        populateFn: () =>
          CopilotManager.populateCarePlans(
            encounterId,
            organizationId,
            countryCode,
          ),
        fieldName: "carePlans",
      },
      {
        data: note.history?.medications?.length
          ? note.history.medications
          : null,
        populateFn: async () => {
          await CopilotManager.populateMedications(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_medications");
        },
        fieldName: "medications",
      },
      {
        data: note.history?.immunizations?.length
          ? note.history.immunizations
          : null,
        populateFn: async () => {
          await CopilotManager.populateImmunizations(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_immunizations");
        },
        fieldName: "immunizations",
      },
      {
        data: note.history?.procedures?.length ? note.history.procedures : null,
        populateFn: async () => {
          await CopilotManager.populateProcedures(
            encounterId,
            organizationId,
            countryCode,
          );
          await refreshCard("copilot_refresh_procedures");
        },
        fieldName: "procedures",
      },
      {
        data: note.plan?.prescriptions?.length ? note.plan.prescriptions : null,
        populateFn: async () => {
          await CopilotManager.populatePrescriptions(
            note.plan!.prescriptions!,
            patientReference,
            encounterReference,
            practitionerReference,
            copilotData.id,
          );
          await refreshCard("copilot_refresh_medications");
        },
        fieldName: "prescriptions",
      },
      {
        data: note.history?.social,
        populateFn: async () => {
          await CopilotManager.populateSocialHistory(
            note.history!.social!,
            patientReference,
            encounterReference,
            practitionerReference,
            copilotData.id,
          );
          await refreshCard("copilot_refresh_history");
        },
        fieldName: "socialHistory",
        refreshHistory: true,
      },
      {
        data: note.history?.sexual,
        populateFn: async () => {
          await CopilotManager.populateSexualHistory(
            note.history!.sexual!,
            patientReference,
            encounterReference,
            practitionerReference,
            copilotData.id,
          );
          await refreshCard("copilot_refresh_history");
        },
        fieldName: "sexualHistory",
        refreshHistory: true,
      },
    ];

    for (const task of populationTasks) {
      await populateField(
        task.data,
        task.populateFn,
        task.fieldName,
        nextStatus,
        task.refreshHistory ? refreshPatientHistory : undefined,
      );
    }

    if (copilotData?.id && encounterReference) {
      const sectionsToClear = (
        Object.keys(nextStatus) as (keyof TCopilotNotePopulationStatus)[]
      )
        .filter((field) => nextStatus[field])
        .flatMap((field) => getSectionsForPopulationField(field));

      const updatedNote = clearSectionsHumanEditedAfterApply(
        {
          ...(copilotData || {}),
          note: {
            ...(copilotData?.note || {}),
            populationStatus: nextStatus,
          },
        },
        sectionsToClear,
        { populationStatus: nextStatus },
      );

      try {
        const updatedCopilotData = await medicalsApi.updateCopilotNote(
          copilotData.id,
          updatedNote,
          encounterHeaders,
        );

        if (updatedCopilotData?.data) {
          setCopilotData(updatedCopilotData.data);
        }
      } catch (error) {
        console.error("Error updating copilot note:", error);
        setNotification({
          message: "Failed to update copilot note",
          variant: "error",
        });
      }
    }
    setPopulating(false);
  };

  const note = copilotData?.note;

  return (
    <Grid container spacing={4} minHeight={"200px"}>
      <Grid item xs={12} minHeight={generating ? "250px" : undefined}>
        {note ? (
          <CopilotNoteDisplay note={note} readOnly={readOnly || generating} />
        ) : generating ? undefined : (
          <Typography>No note generated</Typography>
        )}
      </Grid>

      {!isRecording && copilotData && (
        <CopilotNoteAction
          copilotData={copilotData}
          populating={populating}
          generating={generating}
          readOnly={readOnly}
          hasLinkedEncounter={hasLinkedEncounter}
          onPopulateEncounter={populateEncounter}
          onGenerateNote={generateNote}
        />
      )}
    </Grid>
  );
};

export default CopilotNote;
